import React from 'react';
import * as stylex from '@stylexjs/stylex';
import { useProgressBar } from 'react-aria/useProgressBar';
import { ColorBrand } from '../theme/Colors.stylex';
import { t } from 'src/primitives/I18n';

// MARK: group
// ==========
const THICKNESS_RATIO = 0.115; // stroke width ratio of the svg size
const LAYERS = 24; // smoothness of the animation
const TAIL_MS = 300; // tail fading length
const TRAIL_FADE_POW = 3; // quickness of the tail fading
const REVOLUTION_DURATION_MS = 650; // duration of a full spin

type SpinnerProps = {
  size?: number;
  color?: string;
  inert?: boolean;
  hideTrack?: boolean;
};
function SpinnerComponent({
  size = 25,
  color = ColorBrand.fillDefault,
  inert = false,
  hideTrack = false
}: SpinnerProps) {
  const {
    progressBarProps
  } = useProgressBar({
    isIndeterminate: true,
    'aria-label': t('Loading')
  });
  return <svg {...progressBarProps} width={size} height={size}
  // @ts-expect-error - FIXME: `inert` is not binded as in `react@18.3.1`
  // > https://developer.mozilla.org/fr/docs/Web/HTML/Global_attributes/inert
  inert={inert ? '' : undefined} aria-hidden={inert} className="flexShrink-x2lah0s" data-style-src="wino-gc:src/resources/feedback-indicators/Spinner.tsx:91">
      <g {...stylex.props(styles.group, styles.groupFromProps(inert))}>
        {!hideTrack && <circle cx="50%" cy="50%" r="40%" pathLength={100} {...stylex.props(styles.track, styles.trackFromProps(color, size))} />}
        {Array.from({
        length: LAYERS
      }, (_, index) => {
        const t = index / (LAYERS - 1);
        const opacity = Math.pow(t, TRAIL_FADE_POW);
        const delay = -t * TAIL_MS;
        return <circle key={index} cx="50%" cy="50%" r="40%" pathLength={100} {...stylex.props(styles.layer, styles.layerFromProps(color, size, inert), styles.layerComputed(opacity, delay))} />;
      })}
      </g>
    </svg>;
}
const Spinner = React.memo(SpinnerComponent) as typeof SpinnerComponent;
export { Spinner, type SpinnerProps };

// MARK: style
// ===========
const rotateAnimation = "xt9f89c-B";
const _temp = {
  "willChange-k6sLGO": "willChange-x1b4v5o1",
  "$$css": "wino-gc:src/resources/feedback-indicators/Spinner.tsx:98"
};
const _temp2 = {
  "strokeWidth-kfJifR": "strokeWidth-xsazkwb",
  "$$css": "wino-gc:src/resources/feedback-indicators/Spinner.tsx:105"
};
const _temp3 = {
  "strokeWidth-kfJifR": "strokeWidth-xsazkwb",
  "animationPlayState-k5bvn2": "animationPlayState-x1j3lx0f",
  "$$css": "wino-gc:src/resources/feedback-indicators/Spinner.tsx:121"
};
const _temp4 = {
  "animationDelay-kKxzle": "animationDelay-xvo38ju",
  "$$css": "wino-gc:src/resources/feedback-indicators/Spinner.tsx:126"
};
const styles = {
  group: {
    "transform-k3aq6I": "transform-xvw0k76",
    "transformOrigin-k3nNDw": "transformOrigin-xchxttu",
    $$css: "wino-gc:src/resources/feedback-indicators/Spinner.tsx:94"
  },
  groupFromProps: spinning => [_temp, {
    "--x-willChange": (spinning ? 'transform' : 'auto') != null ? spinning ? 'transform' : 'auto' : undefined
  }],
  track: {
    "fill-kDwRjp": "fill-xbh8q5q",
    "opacity-kSiTet": "opacity-x1xyvc85",
    $$css: "wino-gc:src/resources/feedback-indicators/Spinner.tsx:101"
  },
  trackFromProps: (color, size) => [_temp2, {
    "stroke-kjVXCG": color != null ? "stroke-x6mkzkt" : color,
    $$css: "wino-gc:src/resources/feedback-indicators/Spinner.tsx:105"
  }, {
    "--x-stroke": color != null ? color : undefined,
    "--x-strokeWidth": size * THICKNESS_RATIO * 1.3 != null ? size * THICKNESS_RATIO * 1.3 : undefined
  }],
  layer: {
    "fill-kDwRjp": "fill-xbh8q5q",
    "strokeLinecap-kU5bRw": "strokeLinecap-x37uqsb",
    "strokeDasharray-k5rJSQ": "strokeDasharray-xygcfu0",
    "strokeDashoffset-kDRTOe": "strokeDashoffset-xb69zef",
    "transformBox-kR8GR0": "transformBox-x1764fhq",
    "transformOrigin-k3nNDw": "transformOrigin-x1bndym7",
    "animationName-kKVMdj": "animationName-xh9eo6u",
    "animationDuration-k44tkh": "animationDuration-xeooxyl",
    "animationTimingFunction-kyAemX": "animationTimingFunction-xpwys93",
    "animationIterationCount-ko0y90": "animationIterationCount-xa4qsjk",
    $$css: "wino-gc:src/resources/feedback-indicators/Spinner.tsx:109"
  },
  layerFromProps: (color, size, inert) => [_temp3, {
    "stroke-kjVXCG": color != null ? "stroke-x6mkzkt" : color,
    $$css: "wino-gc:src/resources/feedback-indicators/Spinner.tsx:121"
  }, {
    "--x-stroke": color != null ? color : undefined,
    "--x-strokeWidth": size * THICKNESS_RATIO != null ? size * THICKNESS_RATIO : undefined,
    "--x-animationPlayState": (inert ? 'paused' : 'running') != null ? inert ? 'paused' : 'running' : undefined
  }],
  layerComputed: (opacity, delay) => [_temp4, {
    "opacity-kSiTet": opacity != null ? "opacity-xb4nw82" : opacity,
    $$css: "wino-gc:src/resources/feedback-indicators/Spinner.tsx:126"
  }, {
    "--x-opacity": opacity != null ? opacity : undefined,
    "--x-animationDelay": (val => typeof val === "number" ? val + "ms" : val != null ? val : undefined)(delay + 'ms')
  }]
};